import React, { useEffect, useContext } from 'react';
import { useState } from 'react';
import axios from 'axios';

import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import Backdrop from '@material-ui/core/Backdrop';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';

import { makeStyles, withStyles } from '@material-ui/core/styles';
import FoodBillsTable from './lib/FoodBillsTable';
import PrimaryButton from './lib/buttons/PrimaryButton';
import { Loading } from '../stories/Loading';
import SecondaryButton from './lib/buttons/SecondaryButton';
import NeutralButton from './lib/buttons/NeutralButton';

import { FeatureContext } from '../App';

interface FoodBillsProps {
  accountsView?: boolean;
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: 'auto',
    textAlign: 'start',
    margin: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    textAlign: 'start',
    height: 'calc(100VH - 120px)',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    width: '100VW',
  },
  componentTitle: {
    color: '#142966',
    textAlign: 'center',
    textTransform: 'uppercase',
    fontSize: '24px',
    marginBottom: '0px',
  },
  separatorLine: {
    height: '2px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
    },
    background: '#004878',
    border: 'none',
  },
  foodBillsGrid: {
    alignSelf: 'center',
    marginTop: '12px',
    marginBottom: '12px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
    },
  },
  invoiceTextRoot: {
    borderColor: 'green',
  },
  mainContainer: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      marginLeft: '40px',
      marginRight: '40px',
    },
    borderRadius: '4px',
    marginBottom: '12px',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '12px',
  },
  guestInfoSeparator: {
    background: '#004878',
    width: '2px',
    border: 'none',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      height: '2px',
    },
    [theme.breakpoints.up('sm')]: {
      width: '2px',
      height: 'auto',
    },
  },
  guestInfoDataKey: {
    flex: 1,
    color: '#004878',
    fontSize: '18px',
    fontWeight: 400,
    padding: '8px',
    verticalAlign: 'top',
  },
  guestInfoDataValue: {
    flex: 1,
    color: '#004878',
    fontSize: '18px',
    fontWeight: 700,
    padding: '8px',
    verticalAlign: 'top',
  },
  button: {
    width: '100%',
    maxWidth: '300px',
    marginTop: '24px',
    alignSelf: 'center',
  },
  enhancedTableContainer: {
    marginTop: '24px',
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      marginRight: 0,
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: '40px',
      marginRight: '40px',
    },
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
}));

const RedRadio = withStyles({
  root: {
    '&$checked': {
      color: '#c23616',
    },
  },
  checked: {},
})((props: { [x: string]: any }) => <Radio color="default" {...props} />);

const FoodBills = (props: FoodBillsProps) => {
  const classes = useStyles();
  const features = useContext(FeatureContext);

  const accountsView = props.accountsView || false;
  const [loading, setLoading] = useState(false);
  const [refType, setRefType] = useState<string>();

  const [guestInfo, setGuestInfo] = useState<any>();
  const [foodBills, setFoodBills] = useState<Array<any>>([]);
  const [checkedRooms, setCheckedRooms] = useState<Array<any>>([]);

  const [selectedRoom, setSelectedRoom] = useState<any>();
  const [invoiceId, setInvoiceId] = useState<string>();
  const [selectedBills, setSelectedBills] = useState<any[]>(new Array<any>());

  const [openPaymentOptions, setOpenPaymentOptions] = useState(false);
  const [billType, setBillType] = useState<string>();
  const [payMode, setPayMode] = useState<string>();

  const getAllCheckedRooms = () => {
    setLoading(true);
    axios
      .get('/api/getAllCheckedRooms')
      .then((res) => res.data)
      .then((res) => {
        if (res.err) {
          alert(res.err);
        } else {
          // Ignore caching foodBills from the result to avoid new bills not showing issue.
          const { checkedRooms } = res;
          setCheckedRooms(checkedRooms);
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  };

  const reset = () => {
    setSelectedRoom(null);
    setFoodBills(new Array<any>());
    setGuestInfo(undefined);
    setSelectedBills([]);
    setBillType(undefined);
    setPayMode(undefined);
  };

  const fetchFoodAndGuestInfo = () => {
    if (selectedRoom || invoiceId) {
      let queryString = '';
      switch (refType) {
        case 'room':
          queryString = `checkinInfoId=${selectedRoom.checkin_info_id}`;
          break;

        case 'invoice':
          queryString = `invoiceId=${invoiceId}`;
          break;

        default:
          queryString = '';
          alert('Invalid Selection');
          return;
      }

      setLoading(true);
      axios
        .get(`/api/getFoodBillsDetails?${queryString}`)
        .then((res) => res.data)
        .then((res) => {
          if (res.errno) {
            alert(res.msg);
            setFoodBills([]);
            setSelectedBills([]);
          } else if (
            res.foodTransactions &&
            Array.isArray(res.foodTransactions) &&
            res.foodTransactions.length > 0
          ) {
            setGuestInfo(res.guestInfo);
            setFoodBills(res.foodTransactions);
            setSelectedBills([]);
          } else {
            alert('No bills found');
            setGuestInfo(undefined);
            setFoodBills(new Array<any>());
            setSelectedBills([]);
          }
        })
        .catch((e) => {
          console.log(e);
          alert('Error fetching information!');
          reset();
        })
        .finally(() => setLoading(false));
    } else {
      alert('Enter Missing details');
    }
  };

  const performDeduction = () => {
    if (billType && payMode && selectedBills && selectedBills.length > 0) {
      axios
        .post('/api/addFoodDeduction', {
          billType,
          payMode,
          bills: selectedBills,
        })
        .then((res) => res.data)
        .then((result) => {
          if (result.err) {
            alert(result.err);
          } else {
            console.log('Bills Deducted!');
          }
        })
        .finally(() => {
          setOpenPaymentOptions(false);
          fetchFoodAndGuestInfo();
        })
        .catch((err) => console.log(err));
    }
  };

  useEffect(() => {
    getAllCheckedRooms();
  }, []);

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <Loading />
      </div>
    );
  } else {
    return (
      <div className={classes.root}>
        <h2 className={classes.componentTitle}>Food Bills</h2>
        <hr className={classes.separatorLine} />
        <div className={classes.mainContainer}>
          <div className={classes.formContainer}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Ref Type</FormLabel>
              <RadioGroup
                aria-label="Reference Type"
                value={refType || null}
                onChange={(e) => {
                  reset();
                  setRefType(e.target.value);
                  setInvoiceId(undefined);
                }}
              >
                <FormControlLabel
                  value="room"
                  control={<RedRadio />}
                  label="Room"
                />
                <FormControlLabel
                  value="invoice"
                  control={<RedRadio />}
                  label="Invoice"
                />
              </RadioGroup>
            </FormControl>
            <Autocomplete
              options={checkedRooms.sort((a, b) => a.room_name - b.room_name)}
              groupBy={(r: any) =>
                features.groupRoomsByBlockFeatureEnabled
                  ? r.block_name
                  : r.floor_str
              }
              getOptionLabel={(r: any) => r.room_name}
              style={{ width: 300, marginTop: '24px', marginBottom: '24px' }}
              renderInput={(params: { [x: string]: any }) => (
                <TextField {...params} label="Room No" variant="outlined" />
              )}
              onChange={(_, r) => {
                if (r != null) {
                  setSelectedRoom(r);
                } else {
                  reset();
                }
              }}
              disabled={refType !== 'room'}
              value={selectedRoom || null}
            />
            <TextField
              label="Invoice No"
              variant="outlined"
              placeholder="Ex: 1-21/22"
              disabled={refType !== 'invoice'}
              value={invoiceId || ''}
              onChange={(e) => {
                reset();
                setInvoiceId(e.target.value);
              }}
              style={{ width: 300 }}
            />
            <PrimaryButton
              onClick={fetchFoodAndGuestInfo}
              className={classes.button}
            >
              Fetch
            </PrimaryButton>
          </div>
          <hr className={classes.guestInfoSeparator} />
          <table style={{ flex: 1 }}>
            <tbody>
              <tr>
                <td className={classes.guestInfoDataKey}>Name:</td>
                <td className={classes.guestInfoDataValue}>
                  {guestInfo?.name}
                </td>
              </tr>
              <tr>
                <td className={classes.guestInfoDataKey}>Address:</td>
                <td className={classes.guestInfoDataValue}>
                  {guestInfo?.address}
                </td>
              </tr>
              <tr>
                <td className={classes.guestInfoDataKey}>Phone:</td>
                <td className={classes.guestInfoDataValue}>
                  {guestInfo?.phone}
                </td>
              </tr>
              <tr>
                <td className={classes.guestInfoDataKey}>GST:</td>
                <td className={classes.guestInfoDataValue}>{guestInfo?.gst}</td>
              </tr>
              <tr>
                <td className={classes.guestInfoDataKey}>Check-in Date:</td>
                <td className={classes.guestInfoDataValue}>
                  {guestInfo?.checkinDate}
                </td>
              </tr>
              <tr>
                <td className={classes.guestInfoDataKey}>Check-out Date:</td>
                <td className={classes.guestInfoDataValue}>
                  {guestInfo?.checkoutDate}
                </td>
              </tr>
              <tr>
                <td className={classes.guestInfoDataKey}>Room No:</td>
                <td className={classes.guestInfoDataValue}>
                  {foodBills && foodBills.length > 0
                    ? foodBills[0]._room_no || foodBills[0].room_no
                    : ''}
                </td>
              </tr>
              {features.foodTypeFeatureEnabled ||
              features.packageFeatureEnabled ? (
                <tr>
                  <td className={classes.guestInfoDataKey}>Food Type:</td>
                  <td className={classes.guestInfoDataValue}>
                    {guestInfo?.foodPackage && guestInfo?.foodPackage > 0
                      ? `${guestInfo?.foodBillType} (${guestInfo.foodPackage})`
                      : guestInfo?.foodBillType}
                  </td>
                </tr>
              ) : (
                []
              )}
              <tr>
                <td className={classes.guestInfoDataKey}>Reference:</td>
                <td className={classes.guestInfoDataValue}>
                  {guestInfo?.reference}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <hr className={classes.separatorLine} />
        <div className={classes.enhancedTableContainer}>
          <FoodBillsTable
            data={foodBills}
            paymentDisabled={refType === 'invoice' || accountsView}
            onPayHandler={(selected) => {
              if (refType === 'room') {
                setSelectedBills(selected);
                setOpenPaymentOptions(true);
              }
            }}
          />
        </div>
        <Backdrop className={classes.backdrop} open={openPaymentOptions}>
          <Dialog
            open={openPaymentOptions}
            onClose={() => setOpenPaymentOptions(false)}
            aria-labelledby="form-dialog-title"
          >
            <DialogTitle id="form-dialog-title">Food Payment</DialogTitle>
            <DialogContent>
              <List>
                <ListItem style={{ marginTop: '-24px', marginBottom: '12px' }}>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      aria-label="Bill Type"
                      value={billType || null}
                      onChange={(e) => setBillType(e.target.value)}
                    >
                      <FormControlLabel
                        value="totalBill"
                        control={<RedRadio />}
                        label="Total Bill"
                        labelPlacement="start"
                      />
                      <FormControlLabel
                        value="alcohol"
                        control={<RedRadio />}
                        label="Alcohol (B + L)"
                        labelPlacement="start"
                      />
                    </RadioGroup>
                  </FormControl>
                </ListItem>
                <ListItem>
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel id="demo-simple-select-outlined-label">
                      Pay Mode
                    </InputLabel>
                    <Select
                      labelId="demo-simple-select-outlined-label"
                      id="demo-simple-select-outlined"
                      value={payMode || ''}
                      onChange={(e) => setPayMode(e.target.value as string)}
                      label="Pay Mode"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      <MenuItem value={'Cash'}>Cash</MenuItem>
                      <MenuItem value={'C.C'}>C.C</MenuItem>
                      <MenuItem value={'UPI'}>UPI</MenuItem>
                    </Select>
                  </FormControl>
                </ListItem>
              </List>
            </DialogContent>
            <DialogActions>
              <NeutralButton
                onClick={() => setOpenPaymentOptions(false)}
                className={classes.button}
                style={{ marginTop: '8px' }}
              >
                Cancel
              </NeutralButton>
              <SecondaryButton
                onClick={(_: any) => performDeduction()}
                color="secondary"
                className={classes.button}
                style={{ marginTop: '8px' }}
              >
                Pay
              </SecondaryButton>
            </DialogActions>
          </Dialog>
        </Backdrop>
      </div>
    );
  }
};

export default FoodBills;
